/**
 * hitTest — topmost-element pick over viewer canvas elements.
 *
 * Returns the id of the topmost element hit by the given world point, or null.
 * "Topmost" = highest z; since cellsToCanvasElements returns elements
 * z-ascending, we scan from the end. World coords come from
 * ViewportManager.screenToWorld, so this stays independent of pan/zoom.
 *
 * Hit rules per element:
 *   - 이미지, '보이는 채움'이 있는 도형: bbox — 기존 동작 그대로.
 *   - 내부가 투명한 도형(외곽선·자동 생성 구역·주차구역·그룹 조명 등,
 *     fill 이 none/transparent 이거나 fillOpacity 0): 화면에 실제로 보이는
 *     것만 히트한다 — 테두리 선(stroke) 위, 또는 가운데 이름 라벨 텍스트
 *     영역. 내부 빈 공간 클릭은 아래 깔린 요소로 통과한다.
 *   - strokeTolerance(월드 단위)로 얇은 선도 줌 아웃 상태에서 클릭 가능.
 *
 * 주의: 라벨이 있는 셀은 getCellType 이 type='text' 로 분류하므로 판정은
 * el.type 이 아니라 attrs(body/image/label) 기준으로 한다.
 */

import type { CanvasElement } from './types'

// Path2D 히트 판정용 공유 2D 컨텍스트 (그리지 않음 — isPointInStroke 전용)
let hitCtx: CanvasRenderingContext2D | null = null
function getHitCtx(): CanvasRenderingContext2D | null {
  // jsdom 등 Path2D/2D 컨텍스트가 없는 환경에서는 bbox 판정으로 후퇴
  if (typeof Path2D === 'undefined') return null
  if (!hitCtx) {
    hitCtx = document.createElement('canvas').getContext('2d')
  }
  return hitCtx
}

// d 문자열 → Path2D 캐시. attrs.body 객체 동일성 기준이라 그래프 리로드로
// 요소가 재생성되면 자연히 새로 만들어지고 이전 항목은 GC 된다.
const pathCache = new WeakMap<object, Path2D>()
function getPath(body: { d?: string }): Path2D {
  let path = pathCache.get(body)
  if (!path) {
    path = new Path2D(body.d)
    pathCache.set(body, path)
  }
  return path
}

type BodyAttrs = NonNullable<CanvasElement['attrs']['body']> & { fillOpacity?: number | string }

/** body 채움이 실제로 눈에 보이는지 — none/transparent/fillOpacity 0 은 투명. */
function hasVisibleFill(body: BodyAttrs | undefined): boolean {
  if (!body) return false
  const { fill } = body
  if (!fill || fill === 'none' || fill === 'transparent') return false
  const fo = body.fillOpacity
  if (fo !== undefined && Number(fo) === 0) return false
  return true
}

/** 투명 내부 요소의 '선 위' 판정. localX/Y 는 요소 좌표계. */
function isOnStroke(
  el: CanvasElement,
  localX: number,
  localY: number,
  strokeTolerance: number
): boolean {
  const body = el.attrs.body
  if (!body || !body.stroke || body.stroke === 'none') return false
  const ctx = getHitCtx()
  if (!ctx) return true // 컨텍스트를 못 만들면 기존 bbox 판정으로 후퇴

  let path: Path2D
  if (body.d) {
    path = getPath(body)
  } else {
    // path 데이터 없는 투명 rect(자동 구역·그룹 프레임 등) — 테두리 4변만 히트
    path = new Path2D()
    path.rect(0, 0, el.size.width, el.size.height)
  }
  ctx.lineWidth = Math.max(body.strokeWidth ?? 1, strokeTolerance)
  return ctx.isPointInStroke(path, localX, localY)
}

/**
 * 가운데 이름 라벨 텍스트 영역 판정 — renderPlan 이 라벨을 요소 중앙에
 * 그리므로(cx=w/2, cy=h/2) 대략적인 텍스트 박스로 근사한다. 내부가 투명한
 * 구역도 이름을 클릭하면 선택되도록 하기 위한 것.
 */
function isOnLabel(el: CanvasElement, localX: number, localY: number, pad: number): boolean {
  const text = el.attrs.label?.text
  if (!text) return false
  const fontSize = el.attrs.label?.fontSize || 12
  const halfW = Math.max((String(text).length * fontSize * 0.6) / 2, fontSize) + pad
  const halfH = fontSize * 0.75 + pad
  const cx = el.size.width / 2
  const cy = el.size.height / 2
  return Math.abs(localX - cx) <= halfW && Math.abs(localY - cy) <= halfH
}

export function hitTestElements(
  elements: readonly CanvasElement[],
  worldX: number,
  worldY: number,
  strokeTolerance: number = 0
): string | null {
  for (let i = elements.length - 1; i >= 0; i--) {
    const el = elements[i]
    const { x, y } = el.position
    const { width, height } = el.size
    // 선 폭/허용 오차만큼 bbox 를 넓혀 경계선 클릭이 사전 탈락하지 않게 한다
    const pad = strokeTolerance
    if (worldX < x - pad || worldX > x + width + pad ||
        worldY < y - pad || worldY > y + height + pad) {
      continue
    }

    const body = el.attrs.body as BodyAttrs | undefined
    const solid = !!el.attrs.image?.xlinkHref || hasVisibleFill(body)
    if (solid) {
      // 채움 있는 도형·이미지: 기존 bbox 판정 유지
      if (worldX >= x && worldX <= x + width && worldY >= y && worldY <= y + height) {
        return el.id
      }
      continue
    }

    // 내부 투명: 화면에 보이는 부분(테두리 선, 이름 라벨)만 히트
    const localX = worldX - x
    const localY = worldY - y
    if (isOnLabel(el, localX, localY, strokeTolerance)) return el.id
    if (isOnStroke(el, localX, localY, strokeTolerance)) return el.id
    // 내부 빈 공간 — 아래 깔린 다른 요소에 기회를 준다
  }
  return null
}
